import React, { useCallback, useEffect, useRef, useState } from "react";
import { Input } from "./ui/input";
import API from "../lib/api";

/**
 * Mapbox-powered U.S. address autocomplete used in every profile-edit
 * surface (patient profile, admin Edit User modal, staff profile for
 * non-prescribers). Keeps parity so admins filling an address get the
 * same typo-free suggestions patients do.
 *
 * Props:
 *   value         — current street-address string
 *   onChange(val) — fired on keystroke with the current input text
 *   onSelect({street,city,state,zip,display}) — fired when user picks a suggestion
 *   placeholder   — optional placeholder override
 *   testId        — optional test id (defaults to `address-street`)
 *   className     — optional classes merged onto the Input
 *   disabled      — passthrough disabled flag
 */
export default function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder = "Start typing your address...",
  testId = "address-street",
  className = "rounded-xl",
  disabled = false,
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const search = useCallback(async (q) => {
    if (!q || q.length < 3) { setSuggestions([]); return; }
    setLoading(true);
    try {
      const { data } = await API.get(`/address/autocomplete?q=${encodeURIComponent(q)}`);
      setSuggestions(Array.isArray(data) ? data : []);
      setShowSuggestions(Array.isArray(data) && data.length > 0);
    } catch {
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInput = (val) => {
    onChange?.(val);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => search(val), 400);
  };

  const handleSelect = (s) => {
    onSelect?.(s);
    setShowSuggestions(false);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <Input
        value={value || ""}
        onChange={(e) => handleInput(e.target.value)}
        placeholder={placeholder}
        className={className}
        disabled={disabled}
        data-testid={testId}
        autoComplete="off"
      />
      {loading && (
        <div className="absolute right-3 top-3" data-testid={`${testId}-loading`}>
          <div className="w-4 h-4 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}
      {showSuggestions && suggestions.length > 0 && (
        <div
          className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg max-h-60 overflow-y-auto"
          data-testid={`${testId}-suggestions`}
        >
          {suggestions.map((s, i) => (
            <button
              key={`${s.street}|${s.city}|${s.zip}|${i}`}
              type="button"
              onClick={() => handleSelect(s)}
              className="w-full text-left px-4 py-3 hover:bg-purple-50 dark:hover:bg-purple-900/20 border-b border-slate-100 dark:border-slate-800 last:border-0 text-sm text-slate-700 dark:text-slate-200 transition-colors"
              data-testid={`${testId}-suggestion-${i}`}
            >
              <div className="font-medium">{s.street}</div>
              <div className="text-xs text-slate-500 dark:text-slate-400">
                {s.city}, {s.state} {s.zip}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
